import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import repositoryContainer from '../../../services/RepositoryContainer';

// ─── helpers ──────────────────────────────────────────────────────────────────

const groupPlanBenefits = (benefits) => {
  const map = {};
  benefits.forEach((b) => {
    if (!map[b.plan]) map[b.plan] = [];
    if (b.isVisible) map[b.plan].push(b.displayLabel);
  });
  return map;
};

const PLAN_ORDER = ['Starter', 'Pro', 'Business'];

const PLAN_PRICES = {
  Starter:  { monthly: 0,  yearly: 0 },
  Pro:      { monthly: 12, yearly: 99 },
  Business: { monthly: 25, yearly: 199 },
};

const PLAN_DESC = {
  Starter:  'For new businesses testing the waters.',
  Pro:      'For active day-to-day operations.',
  Business: 'For multi-staff or growing businesses.',
};

const UPFRONT_PAYMENT_MARKS = [0, 30, 50, 70, 100];

// ─── BillingHub ───────────────────────────────────────────────────────────────

const BillingHub = () => {
  const [activeTab, setActiveTab] = useState('subscription');
  const [billingCycle, setBillingCycle] = useState('monthly');

  // ── subscription state ──
  const [subscription, setSubscription] = useState(null);
  const [planBenefits, setPlanBenefits] = useState({});
  const [subLoading, setSubLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // ── payment setup state ──
  const [banks, setBanks] = useState([]);
  const [paymentSetup, setPaymentSetup] = useState(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupForm, setSetupForm] = useState({
    accountNumber: '',
    bankCode: '',
    requiresPayment: false,
    upfrontPaymentPercentage: 100,
  });
  const [setupError, setSetupError] = useState('');
  const [setupSuccess, setSetupSuccess] = useState('');

  // ─── load subscription + plans ────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [sub, plans] = await Promise.all([
          repositoryContainer.subscription.getCurrent(),
          repositoryContainer.subscription.getPlans(),
        ]);
        setSubscription(sub);
        setPlanBenefits(groupPlanBenefits(plans));
      } catch (err) {
        console.error('Failed to load subscription data', err);
      } finally {
        setSubLoading(false);
      }
    };
    load();
  }, []);

  // ─── load payment setup + banks ───────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [banksData, setupData] = await Promise.all([
          repositoryContainer.company.getBanks(),
          repositoryContainer.company.getPaymentSetup(),
        ]);
        const uniqueBanks = [...new Map(banksData.map(b => [b.code, b])).values()];
        setBanks(uniqueBanks);
        setPaymentSetup(setupData);
        setSetupForm({
          accountNumber: setupData.accountNumber || '',
          bankCode: setupData.bankCode || '',
          requiresPayment: setupData.requiresPayment ?? false,
          upfrontPaymentPercentage: Number(setupData.upfrontPaymentPercentage) || 100,
        });
      } catch (err) {
        console.error('Failed to load payment setup', err);
      } finally {
        setSetupLoading(false);
      }
    };
    load();
  }, []);

  // ─── upgrade plan → Paystack ──────────────────────────────────────────────
  const handleUpgrade = async (plan) => {
    if (plan === 'Starter') return;
    setUpgradeLoading(true);
    try {
      const period = billingCycle === 'annual' ? 'Yearly' : 'Monthly';
      const callbackUrl = `${window.location.origin}/subscription/callback`;

      // Persist upgrade intent so the callback page knows what plan to poll for
      localStorage.setItem('clawfe:pendingSubscription', JSON.stringify({
        previousPlan: currentPlan,
        targetPlan: plan,
      }));

      const data = await repositoryContainer.subscription.initiatePayment(plan, period, callbackUrl);
      const raw = data?.response ?? data;
      window.location.href = raw.authorizationUrl;
    } catch (err) {
      localStorage.removeItem('clawfe:pendingSubscription');
      console.error('Subscription payment initiation failed', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  // ─── save payment setup ───────────────────────────────────────────────────
  const handleSaveSetup = async () => {
    setSetupError('');
    setSetupSuccess('');

    if (!paymentSetup?.paymentEnabled && !setupForm.bankCode) {
      setSetupError('Please select a bank.');
      return;
    }
    if (!paymentSetup?.paymentEnabled && !setupForm.accountNumber.trim()) {
      setSetupError('Please enter your account number.');
      return;
    }

    const upfrontPaymentPercentage = Number(setupForm.upfrontPaymentPercentage);
    if (setupForm.requiresPayment && (
      !Number.isFinite(upfrontPaymentPercentage)
      || upfrontPaymentPercentage <= 0
      || upfrontPaymentPercentage > 100
    )) {
      setSetupError('Upfront payment percentage must be greater than 0 and no more than 100.');
      return;
    }

    setSetupSaving(true);
    try {
      if (paymentSetup?.paymentEnabled) {
        await repositoryContainer.company.updatePaymentRequirement({
          requiresPayment: setupForm.requiresPayment,
          upfrontPaymentPercentage,
        });
        setSetupSuccess('Payment requirement updated successfully.');
      } else {
        await repositoryContainer.company.setupPayment({
          accountNumber: setupForm.accountNumber.trim(),
          bankCode: setupForm.bankCode,
          requiresPayment: setupForm.requiresPayment,
          upfrontPaymentPercentage,
          feeBearer: 2,
        });
        setSetupSuccess('Payment setup saved successfully.');
      }
      // refresh setup data
      const updated = await repositoryContainer.company.getPaymentSetup();
      setPaymentSetup(updated);
      setSetupForm({
        accountNumber: updated.accountNumber || '',
        bankCode: updated.bankCode || '',
        requiresPayment: updated.requiresPayment ?? false,
        upfrontPaymentPercentage: Number(updated.upfrontPaymentPercentage) || 100,
      });
    } catch (err) {
      setSetupError(err.data?.message || 'Could not verify bank account. Please check the details and try again.');
    } finally {
      setSetupSaving(false);
    }
  };

  const isAnnual = billingCycle === 'annual';
  const currentPlan = subscription?.plan || 'Starter';
  const currentPlanPrices = PLAN_PRICES[currentPlan] || PLAN_PRICES.Starter;
  const subscriptionPeriod = String(
    subscription?.billingPeriod || subscription?.billingCycle || subscription?.period || 'Monthly'
  );
  const subscriptionIsAnnual = /annual|year/i.test(subscriptionPeriod);
  const currentMonthlyPrice = subscriptionIsAnnual
    ? currentPlanPrices.yearly / 12
    : currentPlanPrices.monthly;
  const currentPriceLabel = currentMonthlyPrice === 0
    ? 'Free'
    : `$${Number.isInteger(currentMonthlyPrice) ? currentMonthlyPrice : currentMonthlyPrice.toFixed(2)}`;
  const currentUsage = [
    { label: 'Bookings', used: subscription?.bookingsThisMonth, limit: subscription?.maxBookingsPerMonth },
    { label: 'Orders', used: subscription?.ordersThisMonth, limit: subscription?.maxOrdersPerMonth },
  ].filter((metric) => metric.used !== undefined);

  const getUsageDetails = ({ used, limit }) => {
    const numericUsed = Number(used) || 0;
    const numericLimit = Number(limit);
    const isUnlimited = !Number.isFinite(numericLimit) || numericLimit >= 2147483647;
    return {
      display: `${numericUsed} of ${isUnlimited ? '∞' : numericLimit}`,
      progress: isUnlimited || numericLimit <= 0
        ? 0
        : Math.min(100, Math.max(0, (numericUsed / numericLimit) * 100)),
    };
  };
  const parsedUpfrontPercentage = Number(setupForm.upfrontPaymentPercentage);
  const displayedUpfrontPercentage = Number.isFinite(parsedUpfrontPercentage)
    ? Math.min(100, Math.max(1, parsedUpfrontPercentage))
    : 1;
  const upfrontTrackProgress = displayedUpfrontPercentage;

  const adjustUpfrontPercentage = (amount) => {
    setSetupForm((current) => {
      const currentValue = Number(current.upfrontPaymentPercentage);
      const nextValue = Math.min(100, Math.max(1, (Number.isFinite(currentValue) ? currentValue : 1) + amount));
      return { ...current, upfrontPaymentPercentage: nextValue };
    });
  };

  return (
    <div className="settings-bento-card overflow-hidden">

      {/* ── Header & Tabs ── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-[var(--color-border)]/30">
        <div>
          <h3 className="settings-card-title">Billing & Growth</h3>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {activeTab === 'payment-setup'
              ? 'Connect your bank account or mobile money to receive payouts. Enable upfront payment to require clients to pay during checkout.'
              : 'View your current plan and upgrade to unlock more bookings, orders, and features.'}
          </p>
        </div>

        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Billing settings"
          className="w-full shrink-0 sm:w-auto"
          options={[
            { value: 'subscription', label: 'Subscription' },
            { value: 'payment-setup', label: 'Pay Setup' },
          ]}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SUBSCRIPTION TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'subscription' && (
        <div className="space-y-12">

          {/* Current status */}
          {subLoading ? (
            <div className="h-36 w-full max-w-xl animate-pulse rounded-xl bg-[var(--color-muted)]" />
          ) : subscription && (
            <section className="w-full max-w-xl rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5 pt-0.5">
                  <span className="rounded bg-[var(--color-foreground)] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--color-background)]">
                    {currentPlan}
                  </span>
                  <span className="text-xs font-semibold text-[var(--color-foreground)]">Plan</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    String(subscription.status).toLowerCase() === 'active'
                      ? 'bg-success/10 text-success'
                      : 'bg-warning/10 text-warning'
                  }`}>
                    {subscription.status}
                  </span>
                </div>

                <div className="flex flex-shrink-0 items-baseline text-right">
                  {currentMonthlyPrice > 0 && <span className="text-sm font-medium text-[var(--color-foreground)]">$</span>}
                  <span className="font-heading text-3xl font-bold leading-none text-[var(--color-foreground)]">
                    {currentMonthlyPrice > 0 ? currentPriceLabel.slice(1) : currentPriceLabel}
                  </span>
                  {currentMonthlyPrice > 0 && <span className="ml-1 text-[10px] text-[var(--color-muted-foreground)]">/month</span>}
                </div>
              </div>

              <div className="mt-4 grid gap-4 border-t border-[var(--color-border)]/60 pt-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <div className="space-y-2.5">
                  {currentUsage.map((metric) => {
                    const usage = getUsageDetails(metric);
                    return (
                      <div key={metric.label} className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-2 text-[11px]">
                        <span className="font-medium text-[var(--color-foreground)]">{metric.label}</span>
                        <div className="h-1.5 min-w-16 max-w-36 overflow-hidden rounded-full bg-[var(--color-primary)]/15">
                          <div
                            className="h-full rounded-full bg-[var(--color-primary)] transition-[width] duration-300"
                            style={{ width: `${usage.progress}%` }}
                          />
                        </div>
                        <span className="whitespace-nowrap text-[var(--color-muted-foreground)]">{usage.display}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-x-5 gap-y-2 text-[10px] sm:justify-end sm:text-right">
                  <div>
                    <p className="text-[var(--color-muted-foreground)]">Next payment</p>
                    <p className="mt-0.5 font-semibold text-[var(--color-foreground)]">
                      {subscription.currentPeriodEnd
                        ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                        : currentMonthlyPrice === 0 ? 'None' : 'Not scheduled'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[var(--color-muted-foreground)]">Billing</p>
                    <p className="mt-0.5 font-semibold text-[var(--color-foreground)]">
                      {currentMonthlyPrice === 0 ? 'Free' : subscriptionIsAnnual ? 'Yearly' : 'Monthly'}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Plan comparison */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[var(--color-primary)]">
                Change Your Plan
              </h4>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${!isAnnual ? 'text-[var(--color-foreground)]' : 'text-[var(--color-muted-foreground)]'}`}>Monthly</span>
                <button
                  onClick={() => setBillingCycle(isAnnual ? 'monthly' : 'annual')}
                  className={`relative w-11 h-6 rounded-full transition-colors ${isAnnual ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-muted)]'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isAnnual ? 'left-6' : 'left-1'}`} />
                </button>
                <span className={`text-xs font-medium ${isAnnual ? 'text-[var(--color-foreground)]' : 'text-[var(--color-muted-foreground)]'}`}>Yearly</span>
                {isAnnual && (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-green-100 text-green-800">Save up to 34%</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PLAN_ORDER.map((plan) => {
                const prices = PLAN_PRICES[plan];
                const isFree = prices.monthly === 0;
                const isCurrentPlan = currentPlan === plan;
                const benefits = planBenefits[plan] || [];
                const savePct = isFree ? 0 : Math.round((1 - prices.yearly / (prices.monthly * 12)) * 100);
                const displayPrice = isFree ? 'Free' : isAnnual ? `$${(prices.yearly / 12).toFixed(2)}` : `$${prices.monthly}`;

                return (
                  <div
                    key={plan}
                    className={`relative p-8 rounded-3xl border-2 transition-all flex flex-col h-full ${
                      isCurrentPlan
                        ? 'border-[var(--color-primary)] bg-[var(--color-card)] shadow-xl'
                        : 'border-[var(--color-border)]/50 bg-transparent'
                    }`}
                  >
                    {plan === 'Pro' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--color-primary)] text-white text-[10px] font-black uppercase px-4 py-1 rounded-full">
                        Most Popular
                      </div>
                    )}

                    <div className="mb-6">
                      <h5 className="text-xl font-serif font-bold text-[var(--color-foreground)]">{plan}</h5>
                      {isAnnual && !isFree && (
                        <p className="text-xs text-[var(--color-muted-foreground)] line-through mt-1">${prices.monthly}/mo</p>
                      )}
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-3xl font-bold text-[var(--color-foreground)]">{displayPrice}</span>
                        {!isFree && <span className="text-xs text-[var(--color-muted-foreground)]">/mo</span>}
                        {isAnnual && !isFree && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800 ml-1">-{savePct}%</span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                        {isFree ? 'free forever' : isAnnual ? `$${prices.yearly} billed yearly` : 'billed monthly'}
                      </p>
                    </div>

                    <p className="text-sm text-[var(--color-muted-foreground)] leading-relaxed mb-6 flex-1">
                      {PLAN_DESC[plan]}
                    </p>

                    {benefits.length > 0 && (
                      <ul className="space-y-3 mb-8">
                        {benefits.map((f) => (
                          <li key={f} className="flex items-center gap-3 text-xs font-medium text-[var(--color-foreground)]/80">
                            <Icon name="Check" size={14} className="text-[var(--color-primary)]" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}

                    <Button
                      variant={isCurrentPlan ? 'default' : 'outline'}
                      className="w-full rounded-xl py-6"
                      disabled={isCurrentPlan || isFree || upgradeLoading}
                      loading={upgradeLoading}
                      onClick={() => handleUpgrade(plan)}
                    >
                      {isCurrentPlan ? 'Current Plan' : isFree ? 'Free Forever' : 'Upgrade'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PAYMENT SETUP TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'payment-setup' && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">

          {setupLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-[var(--color-muted)] animate-pulse rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              {/* Current status banner */}
              {paymentSetup?.paymentEnabled && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                  <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-green-800">Payment processing active</p>
                    <p className="text-xs text-green-700">
                      Account: <span className="font-mono">{paymentSetup.accountNumber}</span>
                      {paymentSetup.accountName && ` — ${paymentSetup.accountName}`}
                    </p>
                  </div>
                </div>
              )}

              {/* Payout notice */}
              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0 rounded-full bg-amber-100 p-2">
                    <Icon name="AlertCircle" size={18} className="text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-3 text-sm leading-relaxed text-amber-900">
                    <div>
                      <p className="font-semibold">Automated payouts</p>
                      <p className="mt-1 text-amber-800">
                        Payouts are only processed on working days, not weekends or public holidays. If a payout falls on a weekend or public holiday, it is processed on the next working day.
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold">Minimum payout amount</p>
                      <p className="mt-1 text-amber-800">
                        The minimum payout amount is GHS 50. This applies to bank-account payouts only; mobile money payouts will continue even below GHS 50. If you have collected less than GHS 50, it will remain in your payout balance until you reach the minimum. Payouts below the minimum may still be paid, but a GHS 5 fee will apply.
                      </p>
                    </div>
                    <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-white/80 px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-2 text-left">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                          <Icon name="ShieldCheck" size={16} />
                        </div>
                        <span className="truncate text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-800">
                          Secure payment
                        </span>
                      </div>
                      <div className="flex flex-shrink-0 flex-wrap items-center gap-2 rounded-full bg-slate-50 px-2.5 py-1">
                        <span className="text-[10px] font-semibold text-slate-600">Powered by</span>
                        <img
                          src="/paystack-logo.png"
                          alt="Paystack"
                          className="h-4 w-auto object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
                {/* Bank select */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[var(--color-primary)] mb-2 block">
                    Bank / Mobile Network
                  </label>
                  <select
                    value={setupForm.bankCode}
                    onChange={(e) => setSetupForm(p => ({ ...p, bankCode: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20 outline-none"
                  >
                    <option value="">Select bank or mobile network</option>
                    {banks.map((b) => (
                      <option key={b.code} value={b.code}>{b.name}</option>
                    ))}
                  </select>
                </div>

                {/* Account number */}
                <Input
                  label="Account Number"
                  placeholder="e.g. 0123456789"
                  value={setupForm.accountNumber}
                  onChange={(e) => setSetupForm(p => ({ ...p, accountNumber: e.target.value }))}
                />
              </div>

              <div className={`grid gap-5 ${setupForm.requiresPayment ? 'lg:grid-cols-[minmax(240px,0.7fr)_minmax(0,1.6fr)] lg:items-start' : ''}`}>
                {/* requiresPayment toggle */}
                <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-muted)]/30 px-3 py-3 lg:max-w-sm">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">Require upfront payment</p>
                    <p className="mt-0.5 text-xs leading-5 text-[var(--color-muted-foreground)]">
                      Collect payment during checkout.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={setupForm.requiresPayment}
                    aria-label="Require upfront payment"
                    onClick={() => setSetupForm(p => ({ ...p, requiresPayment: !p.requiresPayment }))}
                    className={`relative ml-2 h-6 w-11 flex-shrink-0 rounded-full transition-colors ${setupForm.requiresPayment ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-muted)]'}`}
                  >
                    <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all ${setupForm.requiresPayment ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>

                {setupForm.requiresPayment && (
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 sm:p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="max-w-xl">
                      <label
                        htmlFor="upfront-payment-percentage"
                        className="text-sm font-semibold text-[var(--color-foreground)]"
                      >
                        How much should customers pay upfront?
                      </label>
                      <p className="mt-1 text-xs leading-relaxed text-[var(--color-muted-foreground)]">
                        Choose the portion of the total customers pay when they book or place an order. They can pay the rest later.
                      </p>
                    </div>

                    <div className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg bg-[var(--color-primary)]/10 p-1">
                      <button
                        type="button"
                        onClick={() => adjustUpfrontPercentage(-1)}
                        disabled={displayedUpfrontPercentage <= 1}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-primary)] transition-colors hover:bg-[var(--color-card)]/70 disabled:cursor-not-allowed disabled:opacity-35"
                        aria-label="Decrease upfront payment percentage"
                      >
                        <Icon name="Minus" size={15} />
                      </button>
                      <input
                        id="upfront-payment-percentage"
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        inputMode="numeric"
                        value={setupForm.upfrontPaymentPercentage}
                        onChange={(event) => setSetupForm((current) => ({
                          ...current,
                          upfrontPaymentPercentage: event.target.value,
                        }))}
                        className="h-8 w-11 appearance-none rounded-md border border-[var(--color-primary)]/30 bg-[var(--color-card)] px-1 text-center text-sm font-semibold text-[var(--color-foreground)] shadow-sm outline-none transition-shadow [appearance:textfield] focus:ring-2 focus:ring-[var(--color-primary)]/20 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        aria-describedby="upfront-payment-help"
                        aria-label="Upfront payment percentage"
                      />
                      <button
                        type="button"
                        onClick={() => adjustUpfrontPercentage(1)}
                        disabled={displayedUpfrontPercentage >= 100}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-primary)] transition-colors hover:bg-[var(--color-card)]/70 disabled:cursor-not-allowed disabled:opacity-35"
                        aria-label="Increase upfront payment percentage"
                      >
                        <Icon name="Plus" size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-7 px-1">
                    <div className="relative h-7">
                      <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-[var(--color-muted)]">
                        <div
                          className="h-full rounded-full bg-[var(--color-primary)] transition-[width] duration-150"
                          style={{ width: `${upfrontTrackProgress}%` }}
                        />
                      </div>

                      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-2 -translate-y-1/2" aria-hidden="true">
                        {UPFRONT_PAYMENT_MARKS.map((mark) => (
                          <span
                            key={mark}
                            className="absolute top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-[var(--color-card)]/80"
                            style={{ left: `${mark}%` }}
                          />
                        ))}
                      </div>

                      <input
                        type="range"
                        min="1"
                        max="100"
                        step="1"
                        value={displayedUpfrontPercentage}
                        onChange={(event) => setSetupForm((current) => ({
                          ...current,
                          upfrontPaymentPercentage: Number(event.target.value),
                        }))}
                        className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent outline-none [&::-moz-range-progress]:bg-transparent [&::-moz-range-track]:bg-transparent [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-4 [&::-moz-range-thumb]:border-[var(--color-card)] [&::-moz-range-thumb]:bg-[var(--color-primary)] [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:active:cursor-grabbing [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-0.5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-[var(--color-card)] [&::-webkit-slider-thumb]:bg-[var(--color-primary)] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:active:cursor-grabbing"
                        aria-label="Upfront payment percentage"
                        aria-describedby="upfront-payment-help"
                      />
                    </div>

                    <div className="relative mt-1 h-4 text-[10px] font-medium text-[var(--color-muted-foreground)]" aria-hidden="true">
                      {UPFRONT_PAYMENT_MARKS.map((mark) => (
                        <span
                          key={mark}
                          className={`absolute ${mark === 0 ? '' : mark === 100 ? '-translate-x-full' : '-translate-x-1/2'}`}
                          style={{ left: `${mark}%` }}
                        >
                          {mark}%
                        </span>
                      ))}
                    </div>
                  </div>

                  <p id="upfront-payment-help" className="mt-4 text-xs text-[var(--color-muted-foreground)]">
                    Customers will pay {displayedUpfrontPercentage}% now and the remaining {100 - displayedUpfrontPercentage}% later.
                  </p>
                </div>
                )}
              </div>

              {/* Feedback */}
              {setupError && (
                <p className="text-sm text-destructive font-caption">{setupError}</p>
              )}
              {setupSuccess && (
                <p className="text-sm text-green-700 font-caption">{setupSuccess}</p>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2 border-t border-[var(--color-border)]/30">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSetupForm({
                      accountNumber: paymentSetup?.accountNumber || '',
                      bankCode: paymentSetup?.bankCode || '',
                      requiresPayment: paymentSetup?.requiresPayment ?? false,
                      upfrontPaymentPercentage: Number(paymentSetup?.upfrontPaymentPercentage) || 100,
                    });
                    setSetupError('');
                    setSetupSuccess('');
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="default"
                  loading={setupSaving}
                  onClick={handleSaveSetup}
                >
                  {paymentSetup?.paymentEnabled ? 'Save Payment Requirement' : 'Save Payment Setup'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingHub;
